import React from 'react';
import { Phone, MessageSquare, ShieldCheck, ExternalLink } from 'lucide-react';
import useHipaatizerForm from './useHipaatizerForm';
import HiPaaTizerEmbed from './HiPaaTizerEmbed';
import { REQUIRE_HIPAATIZER, FALLBACK_CONTACT } from '@/lib/hipaatizer';

/**
 * Routes a patient-facing form slot through HIPAAtizer.
 *
 * - A HIPAAtizer form is configured for `placement` → render it (iframe, or a
 *   secure link when mode="link", for small widgets).
 * - None configured and REQUIRE_HIPAATIZER → call/text fallback. The native
 *   form (children) is never rendered, so nothing is submitted to this site.
 * - None configured and !REQUIRE_HIPAATIZER → render children (native form).
 */
export default function SecureFormGate({ placement, title, mode = 'embed', compact = false, children, onComplete }) {
  const { form, loading } = useHipaatizerForm(placement);

  if (loading) {
    return <div aria-busy="true" className={compact ? 'p-4 text-xs text-slate-500' : 'rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-500'}>Loading secure form…</div>;
  }

  if (form) {
    if (mode === 'link') {
      return (
        <div className={compact ? 'p-4 space-y-3' : 'rounded-2xl border border-amber-200 bg-white p-5 space-y-3'}>
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-amber-800">
            <ShieldCheck className="h-4 w-4" aria-hidden="true" /> HIPAA-compliant secure form
          </p>
          <p className="text-sm text-slate-600">{title || form.label} opens in a secure window. Your answers go directly to our HIPAA-compliant forms provider and are not stored on this website.</p>
          <a href={form.embed_url} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-amber-700 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-800">
            <ExternalLink className="h-4 w-4" aria-hidden="true" /> Open secure form
          </a>
        </div>
      );
    }
    return <HiPaaTizerEmbed form={form} title={title || form.label} onComplete={onComplete} />;
  }

  if (!REQUIRE_HIPAATIZER) return children ?? null;

  return (
    <div className={compact ? 'p-4 space-y-3' : 'rounded-2xl border border-amber-200 bg-white p-5 text-slate-900 shadow-xl sm:p-7 space-y-3'} role="status">
      <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-amber-800">
        <ShieldCheck className="h-4 w-4" aria-hidden="true" /> Secure form
      </p>
      {!compact && <h2 className="text-2xl font-light">{title || 'Reach our team'}</h2>}
      <p className="text-sm leading-relaxed text-slate-600">Our secure online form is being set up. Our team is available by phone or text.</p>
      <div className={compact ? 'flex flex-col gap-2' : 'flex flex-col gap-3 sm:flex-row'}>
        <a href={FALLBACK_CONTACT.phoneHref} className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-lg bg-amber-700 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-800">
          <Phone className="h-4 w-4" aria-hidden="true" /> Call {FALLBACK_CONTACT.phone}
        </a>
        <a href={FALLBACK_CONTACT.smsHref} className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-lg border border-amber-700 px-4 py-2 text-sm font-semibold text-amber-800 hover:bg-amber-50">
          <MessageSquare className="h-4 w-4" aria-hidden="true" /> Text {FALLBACK_CONTACT.sms}
        </a>
      </div>
      {!compact && <p className="text-xs text-slate-500">Please do not send medical information by text or email.</p>}
    </div>
  );
}
